import * as CookieConsent from "vanilla-cookieconsent";

const CATEGORY_NECESSARY = "necessary";
const CATEGORY_ANALYTICS = "analytics";
const SERVICE_ANALYTICS_STORAGE = "analytics_storage";
const GRANTED = "granted";
const DENIED = "denied";

let initialized = false;

const translation = {
  en: {
    consentModal: {
      title: "We use cookies",
      description: "We use technically necessary cookies and, with your consent, analytics cookies to improve this website.",
      acceptAllBtn: "Accept all",
      acceptNecessaryBtn: "Reject all",
      showPreferencesBtn: "Manage preferences"
    },
    preferencesModal: {
      title: "Cookie preferences",
      acceptAllBtn: "Accept all",
      acceptNecessaryBtn: "Reject all",
      savePreferencesBtn: "Save preferences",
      closeIconLabel: "Close",
      sections: [
        {
          title: "Cookie usage",
          description: "Necessary cookies keep the website functional. Analytics cookies help us understand usage patterns."
        },
        {
          title: "Strictly necessary",
          description: "Required for core website functionality and cannot be disabled.",
          linkedCategory: CATEGORY_NECESSARY
        },
        {
          title: "Analytics",
          description: "Helps us improve the website using aggregated usage statistics.",
          linkedCategory: CATEGORY_ANALYTICS
        }
      ]
    }
  },
  de: {
    consentModal: {
      title: "Wir verwenden Cookies",
      description: "Wir verwenden technisch notwendige Cookies und mit Ihrer Einwilligung Analyse-Cookies zur Verbesserung dieser Website.",
      acceptAllBtn: "Alle akzeptieren",
      acceptNecessaryBtn: "Alle ablehnen",
      showPreferencesBtn: "Einstellungen verwalten"
    },
    preferencesModal: {
      title: "Cookie-Einstellungen",
      acceptAllBtn: "Alle akzeptieren",
      acceptNecessaryBtn: "Alle ablehnen",
      savePreferencesBtn: "Einstellungen speichern",
      closeIconLabel: "Schließen",
      sections: [
        {
          title: "Cookie-Nutzung",
          description: "Notwendige Cookies halten die Website funktionsfähig. Analyse-Cookies helfen uns, die Nutzung besser zu verstehen."
        },
        {
          title: "Technisch notwendig",
          description: "Für Kernfunktionen der Website erforderlich und nicht deaktivierbar.",
          linkedCategory: CATEGORY_NECESSARY
        },
        {
          title: "Analyse",
          description: "Hilft uns, die Website anhand aggregierter Nutzungsdaten zu verbessern.",
          linkedCategory: CATEGORY_ANALYTICS
        }
      ]
    }
  },
  fr: {
    consentModal: {
      title: "Nous utilisons des cookies",
      description: "Nous utilisons des cookies strictement nécessaires et, avec votre consentement, des cookies d'analyse pour améliorer ce site.",
      acceptAllBtn: "Tout accepter",
      acceptNecessaryBtn: "Tout refuser",
      showPreferencesBtn: "Gérer les préférences"
    },
    preferencesModal: {
      title: "Préférences des cookies",
      acceptAllBtn: "Tout accepter",
      acceptNecessaryBtn: "Tout refuser",
      savePreferencesBtn: "Enregistrer les préférences",
      closeIconLabel: "Fermer",
      sections: [
        {
          title: "Utilisation des cookies",
          description: "Les cookies nécessaires assurent le bon fonctionnement du site. Les cookies d'analyse nous aident à comprendre son utilisation."
        },
        {
          title: "Strictement nécessaires",
          description: "Requis pour les fonctionnalités essentielles du site et ne peuvent pas être désactivés.",
          linkedCategory: CATEGORY_NECESSARY
        },
        {
          title: "Analyse",
          description: "Nous aide à améliorer le site grâce à des statistiques d'usage agrégées.",
          linkedCategory: CATEGORY_ANALYTICS
        }
      ]
    }
  }
};

function currentLocale() {
  const locale = (document.documentElement.lang || "de").toLowerCase();
  return Object.prototype.hasOwnProperty.call(translation, locale) ? locale : "de";
}

export function measurementId() {
  return document.documentElement.dataset.gaMeasurementId || "";
}

function ensureGtagBootstrap() {
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== "function") {
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
  }
}

function setConsentDefaults() {
  window.gtag("consent", "default", {
    ad_storage: DENIED,
    ad_user_data: DENIED,
    ad_personalization: DENIED,
    analytics_storage: DENIED
  });
}

export function analyticsConsentGranted(consent = CookieConsent) {
  return consent.acceptedService(SERVICE_ANALYTICS_STORAGE, CATEGORY_ANALYTICS);
}

function updateGoogleConsent(CookieConsent) {
  const analytics = analyticsConsentGranted(CookieConsent) ? GRANTED : DENIED;

  window.gtag("consent", "update", {
    ad_storage: DENIED,
    ad_user_data: DENIED,
    ad_personalization: DENIED,
    analytics_storage: analytics
  });

  if (analytics === GRANTED) {
    bootGoogleAnalytics();
  }
}

function bootGoogleAnalytics() {
  const id = measurementId();
  if (!id || window.__vpiGaInitialized) return;

  window.__vpiGaInitialized = true;

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
  document.head.appendChild(script);

  window.gtag("js", new Date());
  window.gtag("config", id, {
    anonymize_ip: true,
    allow_google_signals: false,
    allow_ad_personalization_signals: false
  });
}

export function initCookieConsent() {
  if (initialized) return;

  ensureGtagBootstrap();
  setConsentDefaults();

  CookieConsent.run({
    root: document.documentElement,
    categories: {
      [CATEGORY_NECESSARY]: {
        enabled: true,
        readOnly: true
      },
      [CATEGORY_ANALYTICS]: {
        autoClear: {
          cookies: [
            { name: /^_ga/ },
            { name: "_gid" },
            { name: "_gat" }
          ]
        },
        services: {
          [SERVICE_ANALYTICS_STORAGE]: {
            label: "Google Analytics"
          }
        }
      }
    },
    language: {
      default: currentLocale(),
      translations: translation
    },
    onFirstConsent: () => updateGoogleConsent(CookieConsent),
    onConsent: () => updateGoogleConsent(CookieConsent),
    onChange: () => updateGoogleConsent(CookieConsent)
  });

  initialized = true;
}
